/**
 * AWB input handling shared by every scan / paste workflow (DRS, transfers, returns, status changes…).
 */
export interface ParsedAwbs {
  awbs: string[]
  invalid: string[]
  duplicates: number
}

const AWB_PATTERN = /^[A-Za-z0-9-]{4,30}$/

export function parseAwbs(input: string): ParsedAwbs {
  const tokens = input.split(/[\s,;|]+/).map((s) => s.trim()).filter(Boolean)
  // the code as it was typed, each once whatever its case: Dispatch finds «10346533-Copy» but not «10346533-COPY»
  // (owner, 2026-10-28: «10346533-Copy عايزه اضرب الكود ده علي مندوب … السيستم مش راضي»); a plain code (ALS1234567,
  // als1234567) is still written in capitals as before
  const seen = new Map<string, string>()
  const invalid: string[] = []
  let duplicates = 0
  for (const tok of tokens) {
    if (!AWB_PATTERN.test(tok)) {
      invalid.push(tok)
      continue
    }
    const key = tok.toUpperCase()
    if (seen.has(key)) duplicates++
    else seen.set(key, /^[A-Za-z]{0,6}\d+$/.test(tok) ? key : tok)
  }
  return { awbs: [...seen.values()], invalid, duplicates }
}

/** Dispatch list parameters (`awBs`, `awbs`) take a comma-separated string. */
export const joinAwbs = (awbs: string[]) => awbs.join(',')

/* ------------------------------------------------------------------ mining pasted text */

/**
 * Staff paste whole rows out of a sheet or a WhatsApp message, not clean lists — "ALS0156174,01050204400,
 * 01030210397,CAIRO,RETURNED" is one row of a Detail Inquiry export. `parseAwbs` above takes every token that
 * looks like a word or a number, so the phones, the city and the status all went to Dispatch as AWBs and it
 * answered "Invalid AWBs" (live audit log, 2026-09-20).
 *
 * `mineAwbs` picks the real codes out of that text the way the Detail Inquiry bulk box does: a letter prefix
 * with digits (ALS4267045, MG6253423-R) or a bare long number, Egyptian mobiles pulled out separately, and
 * everything else (words, cities, statuses, dates, money) reported as ignored so the screen can show what it
 * understood before anything is sent.
 */
// a copy keeps its suffix: «10346533-Copy» is its own shipment, not 10346533 (owner, 2026-10-28)
export const CODE_IN_TEXT = /[A-Za-z]{2,6}[-_]?\d{4,14}(?:-[A-Za-z0-9]{1,6})?|\d{6,15}(?:-[A-Za-z]{1,6})?/g
/** ٠١٠… / ۰۱۰… typed on an Arabic keyboard: read as the digits they are, or no mobile is ever recognised in them. */
export const latinDigits = (t: string) => t.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
/** Egyptian mobile, however it was written (+20, 0020, 20, or plain 01…). */
export const MOBILE_RE = /^(?:\+?2|00?2)?0?1[0125]\d{8}$/
/** A token that can be a code on its own: the AWB shape, and it must carry at least one digit (CAIRO never is one). */
const CODE_TOKEN = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9-]{3,29}$/
const TOKEN_SPLIT = /[\s,;|،]+/

export const isMobileToken = (t: string) => MOBILE_RE.test(t.replace(/\D/g, ''))
/** "+201050204400" / "00201050204400" → "01050204400". */
export const localMobile = (v: string) => v.replace(/\D/g, '').replace(/^(00?2|2)/, '').replace(/^1/, '01')

export interface MinedAwbs {
  /** The codes, upper-cased and de-duplicated, capped at `limit`. */
  awbs: string[]
  /** Egyptian mobiles found in the text — never a code, and the reason "Invalid AWBs" came back. */
  mobiles: string[]
  /** Tokens that are plainly not codes (words, statuses, cities, prices) — shown so the user sees what was dropped. */
  ignored: string[]
  /** How many codes were dropped because of `limit`. */
  over: number
}

export function mineAwbs(text: string, limit = 500): MinedAwbs {
  const tokens = String(text ?? '').split(TOKEN_SPLIT).map((t) => t.trim()).filter(Boolean)
  const mobiles = [...new Set(tokens.filter(isMobileToken).map(localMobile))]
  const rest = tokens.filter((t) => !isMobileToken(t))
  // a clean list is taken as typed (a wrong code still shows as wrong); anything with a stray word in it is mined
  const looksList = rest.length > 0 && rest.every((t) => CODE_TOKEN.test(t))
  /*
   * Codes that mix letters and digits (Line's L1S7846) are not "letters then digits", so in a message they are taken
   * as whole words: Latin letters and digits together, six characters or more (owner, 2026-10-08).
   */
  const mixed = looksList ? [] : rest.map((t) => t.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '')).filter((t) => t.length >= 6 && /[A-Za-z]/.test(t) && /\d/.test(t) && CODE_TOKEN.test(t))
  const found = looksList ? rest : [...(text.match(CODE_IN_TEXT) ?? []).filter((t) => !isMobileToken(t)), ...mixed]
  const seen = new Set<string>()
  const kept = new Map<string, string>()
  for (const t of found) if (CODE_TOKEN.test(t) && !kept.has(t.toUpperCase())) kept.set(t.toUpperCase(), /^[A-Za-z]{0,6}\d+$/.test(t) ? t.toUpperCase() : t)
  for (const k of kept.keys()) seen.add(k)
  const all = [...kept.values()]
  const awbs = all.slice(0, limit)
  // a token is "ignored" only when no code was mined out of it at all ("AWB:ALS123456" gave one, "CAIRO" did not)
  const ignored = [...new Set(rest.filter((t) => !all.some((code) => t.toUpperCase().includes(code.toUpperCase()))))]
  return { awbs, mobiles, ignored, over: Math.max(0, all.length - awbs.length) }
}
